import Hero from "../components/Hero";
import hotelImg from "../assests/images/hotel-bg.jpg";
import {
    FaShieldAlt,
    FaLock,
    FaUserSecret,
    FaCookieBite,
    FaInfoCircle,
    FaChevronRight,
    FaCheckCircle,
    FaArrowRight,
} from "react-icons/fa";

export default function PrivacyPolicy() {
    const quickLinks = [
        "Introduction",
        "Information We Collect",
        "How We Use Data",
        "Security",
        "Sharing",
        "Cookies",
        "Your Rights",
        "Contact",
    ];

    return (
        <div className="w-full bg-[#F7F5EF]">

            {/* =====================================================
                HERO
            ===================================================== */}
            <Hero
                variant="inner"
                title="Privacy Policy"
                subtitle="Your privacy matters to us. Learn how we collect, use and protect your information."
                backgroundImage={hotelImg}
            />


            {/* =====================================================
                PRIVACY INTRO
            ===================================================== */}
            <section className="relative overflow-hidden bg-[#F7F5EF] px-5 py-14 sm:px-8 sm:py-16 lg:px-12 lg:py-20 xl:px-16">

                {/* Decorative Elements */}

                <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-[#0B5552]/5 blur-3xl" />

                <div className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-[#B78932]/5 blur-3xl" />


                <div className="relative z-10 mx-auto max-w-[1440px]">

                    <div className="grid gap-8 lg:grid-cols-[1.35fr_0.65fr]">

                        {/* =================================================
                            MAIN INTRO
                        ================================================= */}
                        <div className="relative overflow-hidden rounded-[28px] border border-[#DED8CC] bg-[#FBFAF6] p-7 shadow-[0_15px_45px_rgba(23,63,62,0.06)] sm:p-9 lg:p-10">

                            {/* Gold Accent */}

                            <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-[#0B5552] to-[#B78932]" />


                            <div className="flex flex-col gap-5 sm:flex-row sm:items-start">

                                {/* Icon */}

                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#F3EBDD] text-[#0B5552]">
                                    <FaShieldAlt className="text-xl" />
                                </div>


                                <div>

                                    {/* Eyebrow */}

                                    <div className="flex items-center gap-3">

                                        <span className="h-px w-7 bg-[#B78932]" />

                                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0B5552]">
                                            Your Privacy
                                        </span>

                                    </div>


                                    {/* Heading */}

                                    <h2 className="mt-3 font-serif text-2xl leading-tight text-[#173F3E] sm:text-3xl lg:text-[34px]">
                                        Your Information Matters
                                    </h2>


                                    {/* Description */}

                                    <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-500 sm:text-base">
                                        We are committed to protecting your
                                        personal information and maintaining
                                        your trust. This Privacy Policy explains
                                        how we collect, use and safeguard the
                                        information you provide when using our
                                        website and services.
                                    </p>

                                </div>

                            </div>

                        </div>


                        {/* =================================================
                            TRUST CARD
                        ================================================= */}
                        <div className="relative overflow-hidden rounded-[28px] bg-[#173F3E] p-7 text-white shadow-[0_18px_50px_rgba(23,63,62,0.16)] sm:p-8">

                            {/* Decorative Circle */}

                            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#0B5552]/50 blur-2xl" />

                            <div className="pointer-events-none absolute -bottom-20 -left-20 h-48 w-48 rounded-full bg-[#B78932]/10 blur-3xl" />


                            <div className="relative z-10">

                                {/* Icon */}

                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#D5A33C] text-[#173F3E]">
                                    <FaLock />
                                </div>


                                <h3 className="mt-5 font-serif text-xl !text-white">
                                    Safe & Secure
                                </h3>


                                <p className="mt-2 text-sm leading-6 text-white/65">
                                    We take reasonable measures to help keep
                                    your personal information secure.
                                </p>


                                {/* Trust Points */}

                                <div className="mt-6 space-y-3.5">

                                    {[
                                        "Secure information handling",
                                        "Trusted service partners",
                                        "Privacy-focused practices",
                                    ].map((item, index) => (

                                        <div
                                            key={index}
                                            className="flex items-center gap-3"
                                        >

                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10">
                                                <FaCheckCircle className="text-[10px] text-[#D5A33C]" />
                                            </span>

                                            <span className="text-xs text-white/75">
                                                {item}
                                            </span>

                                        </div>

                                    ))}

                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </section>


            {/* =====================================================
                MAIN POLICY AREA
            ===================================================== */}
            <section className="bg-[#F7F5EF] px-5 pb-16 sm:px-8 sm:pb-20 lg:px-12 lg:pb-24 xl:px-16">

                <div className="mx-auto max-w-[1440px]">

                    <div className="grid gap-8 lg:grid-cols-[260px_1fr]">


                        {/* =================================================
                            SIDEBAR
                        ================================================= */}
                        <aside className="hidden lg:block">

                            <div className="sticky top-24 rounded-[24px] border border-[#DED8CC] bg-[#FBFAF6] p-5 shadow-[0_10px_35px_rgba(23,63,62,0.05)]">

                                {/* Sidebar Heading */}

                                <div className="mb-5">

                                    <div className="flex items-center gap-2">

                                        <span className="h-px w-6 bg-[#B78932]" />

                                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B5552]">
                                            On This Page
                                        </p>

                                    </div>

                                    <h3 className="mt-2 font-serif text-lg text-[#173F3E]">
                                        Quick Navigation
                                    </h3>

                                </div>


                                {/* Navigation */}

                                <div className="space-y-1">

                                    {quickLinks.map((item, index) => (

                                        <a
                                            key={index}
                                            href={`#section-${index}`}
                                            className="
                                                group
                                                flex
                                                items-center
                                                gap-3
                                                rounded-xl
                                                px-3
                                                py-2.5
                                                text-xs
                                                font-semibold
                                                text-slate-500
                                                transition-all
                                                duration-200
                                                hover:bg-[#F3EBDD]
                                                hover:text-[#0B5552]
                                            "
                                        >

                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#F3EBDD] text-[9px] font-bold text-[#0B5552] transition-all duration-200 group-hover:bg-[#173F3E] group-hover:text-[#D5A33C]">
                                                {String(index + 1).padStart(2, "0")}
                                            </span>

                                            <span className="flex-1">
                                                {item}
                                            </span>

                                            <FaChevronRight className="text-[8px] opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100" />

                                        </a>

                                    ))}

                                </div>

                            </div>

                        </aside>


                        {/* =================================================
                            POLICY CONTENT
                        ================================================= */}
                        <div>

                            {/* =================================================
                                MOBILE NAVIGATION
                            ================================================= */}
                            <div className="mb-6 lg:hidden">

                                <details className="group overflow-hidden rounded-2xl border border-[#DED8CC] bg-[#FBFAF6] shadow-[0_8px_25px_rgba(23,63,62,0.04)]">

                                    <summary className="flex cursor-pointer list-none items-center justify-between px-5 py-4 text-sm font-bold text-[#173F3E]">

                                        <span>
                                            Privacy Policy Sections
                                        </span>

                                        <FaChevronRight className="text-xs text-[#0B5552] transition-transform duration-200 group-open:rotate-90" />

                                    </summary>


                                    <div className="border-t border-[#DED8CC] p-3">

                                        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">

                                            {quickLinks.map((item, index) => (

                                                <a
                                                    key={index}
                                                    href={`#section-${index}`}
                                                    className="group flex items-center gap-3 rounded-xl px-3 py-3 text-xs font-semibold text-slate-500 transition-all duration-200 hover:bg-[#F3EBDD] hover:text-[#0B5552]"
                                                >

                                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F3EBDD] text-[9px] font-bold text-[#0B5552] group-hover:bg-[#173F3E] group-hover:text-[#D5A33C]">
                                                        {String(index + 1).padStart(2, "0")}
                                                    </span>

                                                    {item}

                                                </a>

                                            ))}

                                        </div>

                                    </div>

                                </details>

                            </div>


                            {/* =================================================
                                CONTENT CARD
                            ================================================= */}
                            <div className="overflow-hidden rounded-[28px] border border-[#DED8CC] bg-[#FBFAF6] shadow-[0_15px_50px_rgba(23,63,62,0.06)]">

                                {/* Top Gold/Teal Accent */}

                                <div className="h-1 w-full bg-gradient-to-r from-[#173F3E] via-[#0B5552] to-[#B78932]" />


                                <div className="p-6 sm:p-8 lg:p-10 xl:p-12">

                                    <div className="space-y-0">

                                        {/* =================================================
                                            01 — INTRODUCTION
                                        ================================================= */}

                                        <Section
                                            id="section-0"
                                            number="01"
                                            icon={<FaInfoCircle />}
                                            title="Introduction"
                                            text="We value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data."
                                        />


                                        {/* =================================================
                                            02 — INFORMATION WE COLLECT
                                        ================================================= */}

                                        <Section
                                            id="section-1"
                                            number="02"
                                            icon={<FaUserSecret />}
                                            title="Information We Collect"
                                            list={[
                                                "Personal details such as name, email and phone number",
                                                "Booking and travel preferences",
                                                "Payment information handled through secure systems",
                                                "Usage data including cookies and analytics information",
                                            ]}
                                        />


                                        {/* =================================================
                                            03 — HOW WE USE DATA
                                        ================================================= */}

                                        <Section
                                            id="section-2"
                                            number="03"
                                            icon={<FaInfoCircle />}
                                            title="How We Use Your Information"
                                            list={[
                                                "Process bookings and payments",
                                                "Improve our website and user experience",
                                                "Send booking confirmations and relevant communications",
                                                "Provide customer support and respond to enquiries",
                                            ]}
                                        />


                                        {/* =================================================
                                            04 — SECURITY
                                        ================================================= */}

                                        <Section
                                            id="section-3"
                                            number="04"
                                            icon={<FaLock />}
                                            title="Data Security"
                                            text="We use reasonable security measures, including secure systems and appropriate safeguards, to help protect your personal information from unauthorized access, misuse or disclosure."
                                        />


                                        {/* =================================================
                                            05 — SHARING
                                        ================================================= */}

                                        <Section
                                            id="section-4"
                                            number="05"
                                            icon={<FaShieldAlt />}
                                            title="Sharing of Information"
                                            text="We do not sell your personal information. Information may be shared with trusted service providers or partners when necessary to provide our services, process bookings or support your requests."
                                        />


                                        {/* =================================================
                                            06 — COOKIES
                                        ================================================= */}

                                        <Section
                                            id="section-5"
                                            number="06"
                                            icon={<FaCookieBite />}
                                            title="Cookies"
                                            text="We use cookies and similar technologies to improve your browsing experience, understand website usage and support certain website functionality. You can manage cookies through your browser settings."
                                        />


                                        {/* =================================================
                                            07 — YOUR RIGHTS
                                        ================================================= */}

                                        <Section
                                            id="section-6"
                                            number="07"
                                            icon={<FaUserSecret />}
                                            title="Your Rights"
                                            text="You may request access to, correction of, or deletion of your personal information where applicable. You can contact our team if you have questions or requests regarding your personal data."
                                        />


                                        {/* =================================================
                                            08 — CONTACT
                                        ================================================= */}

                                        <Section
                                            id="section-7"
                                            number="08"
                                            icon={<FaInfoCircle />}
                                            title="Contact Us"
                                            text="If you have any questions or concerns about this Privacy Policy or how your information is handled, please contact our support team."
                                            last
                                        />

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </section>


            {/* =====================================================
                FINAL PRIVACY BANNER
            ===================================================== */}
            <section className="bg-[#F7F5EF] px-5 pb-16 sm:px-8 sm:pb-20 lg:px-12 lg:pb-24 xl:px-16">

                <div className="mx-auto max-w-[1440px]">

                    <div className="relative overflow-hidden rounded-[30px] bg-[#173F3E] px-6 py-11 text-center shadow-[0_20px_60px_rgba(23,63,62,0.15)] sm:px-10 sm:py-14">

                        {/* Decorative Elements */}

                        <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-[#0B5552]/50 blur-3xl" />

                        <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-[#B78932]/15 blur-3xl" />

                        <div className="pointer-events-none absolute left-1/2 top-0 h-40 w-40 -translate-x-1/2 rounded-full bg-white/5 blur-3xl" />


                        <div className="relative z-10 mx-auto max-w-2xl">

                            {/* Icon */}

                            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#D5A33C] text-xl text-[#173F3E] shadow-lg">
                                <FaShieldAlt />
                            </div>


                            {/* Eyebrow */}

                            <div className="mt-6 flex items-center justify-center gap-3">

                                <span className="h-px w-7 bg-[#B78932]" />

                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D5A33C]">
                                    Your Privacy
                                </span>

                                <span className="h-px w-7 bg-[#B78932]" />

                            </div>


                            {/* Heading */}

                            <h2 className="mt-4 font-serif text-2xl leading-tight !text-white sm:text-3xl lg:text-[36px]">
                                Your Privacy Is Important To Us
                            </h2>


                            {/* Description */}

                            <p className="mt-4 text-sm leading-7 text-white/65 sm:text-base">
                                We are committed to handling your information
                                responsibly and providing a safe experience
                                throughout your journey with us.
                            </p>


                            {/* Button */}

                            <a
                                href="/contact-us"
                                className="group mt-8 inline-flex items-center gap-3 rounded-full bg-[#D5A33C] px-6 py-3.5 text-sm font-bold text-[#173F3E] shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B78932] hover:shadow-xl"
                            >

                                Contact Our Team

                                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#173F3E] text-[#D5A33C] transition-transform duration-300 group-hover:translate-x-1">
                                    <FaArrowRight className="text-[10px]" />
                                </span>

                            </a>

                        </div>

                    </div>

                </div>

            </section>

        </div>
    );
}


/* =========================================================
   REUSABLE POLICY SECTION
========================================================= */

function Section({
    id,
    number,
    icon,
    title,
    text,
    list,
    last = false,
}) {
    return (
        <div
            id={id}
            className={`scroll-mt-28 ${!last
                ? "mb-9 border-b border-[#E7DED0] pb-9"
                : ""
                }`}
        >

            {/* =================================================
                HEADING
            ================================================= */}

            <div className="flex items-start gap-4 sm:gap-5">

                {/* Number */}

                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F3EBDD] text-xs font-bold text-[#0B5552]">
                    {number}
                </div>


                <div className="min-w-0 flex-1">

                    <div className="flex items-center gap-2.5">

                        {/* Icon */}

                        <span className="text-sm text-[#B78932]">
                            {icon}
                        </span>


                        {/* Title */}

                        <h2 className="font-serif text-xl leading-tight text-[#173F3E] sm:text-[23px]">
                            {title}
                        </h2>

                    </div>


                    {/* =================================================
                        TEXT
                    ================================================= */}

                    {text && (
                        <p className="mt-4 text-sm leading-7 text-slate-500 sm:text-[15px]">
                            {text}
                        </p>
                    )}


                    {/* =================================================
                        LIST
                    ================================================= */}

                    {list && (
                        <div className="mt-5 space-y-3">

                            {list.map((item, index) => (

                                <div
                                    key={index}
                                    className="flex items-start gap-3"
                                >

                                    <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F3EBDD]">

                                        <FaCheckCircle className="text-[8px] text-[#0B5552]" />

                                    </span>

                                    <p className="text-sm leading-6 text-slate-500 sm:text-[15px]">
                                        {item}
                                    </p>

                                </div>

                            ))}

                        </div>
                    )}

                </div>

            </div>

        </div>
    );
}